import {
  EmailsInput,
  EmailsInputSchema,
  QuoteContext,
  RewriteOption,
  useAIGenerator,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  AIEditOptions,
  FlexBox,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { LeftArrowIcon, MailIcon } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FormProvider,
  useForm,
} from 'react-hook-form';
import z from 'zod';
import { type ContactInfo } from '../../../../data-access/appointments';
import FollowUpChannelCard from '../follow-up-section/FollowUpChannelCard';
import { emailContactsOf } from '../follow-up-section/followUpMessage.utils';
import FollowUpNativeActions, {
  type FollowUpChannel,
} from '../follow-up-section/FollowUpNativeActions';
import { PhonesInputSchema } from '../follow-up-section/FollowUpPhonesInput';

const FollowUpSchema = z
  .object({ followUpMessage: z.string().min(1) })
  .merge(EmailsInputSchema)
  .merge(PhonesInputSchema)
  .refine((data) => data.emails.length > 0, {
    error: 'Select at least one email',
    path: ['emails'],
  });

export type FollowUpSectionInputs = z.infer<typeof FollowUpSchema>;

type FollowUpSectionProps = {
  defaultValues: FollowUpSectionInputs;
  contacts: ContactInfo[];
  isLoadingChannel?: FollowUpChannel;
  getShareLink: (channel: FollowUpChannel) => Promise<string | null>;
  formId?: string;
  onSubmit: (data: FollowUpSectionInputs) => Promise<void>;
  engagementContext?: {
    organizationId: string;
    appointmentId: string;
    appointmentProposalId: string;
  };
  isLoading?: boolean;
};

const FollowUpSection: React.FC<FollowUpSectionProps> = ({
  defaultValues,
  contacts,
  isLoadingChannel,
  getShareLink,
  isLoading,
  formId,
  onSubmit,
  engagementContext,
}) => {
  const { showToast } = useToast();
  const { isLoadingOption: isGenerating, generateQuote } = useAIGenerator();

  const [previousFollowUpMessage, setPreviousFollowUpMessage] = useState<
    string | null
  >(null);
  const methods = useForm<FollowUpSectionInputs>({
    resolver: zodResolver(FollowUpSchema),
    defaultValues,
  });
  const { control, handleSubmit } = methods;

  const improveFollowUpMessageHandler = async (
    field: ControllerRenderProps<FollowUpSectionInputs, 'followUpMessage'>,
    rewriteOption: RewriteOption
  ) => {
    const message = field.value;

    if (!message) {
      showToast('Please add a message to improve', 'info');
      return;
    }
    const result = await generateQuote(
      message,
      rewriteOption,
      QuoteContext.PROPOSAL_FOLLOW_UP_MESSAGE,
      engagementContext
    );
    if (result.length) {
      setPreviousFollowUpMessage(message);
      field.onChange(result.map((r) => r.improvedQuote).join(' '));
      showToast('Your description has been improved!', 'success');
    } else {
      showToast(
        'We were unable to generate an improved description',
        'warning'
      );
    }
  };

  const handleUndo = (
    field: ControllerRenderProps<FollowUpSectionInputs, 'followUpMessage'>
  ) => {
    if (previousFollowUpMessage) {
      field.onChange(previousFollowUpMessage);
      setPreviousFollowUpMessage(null);
      showToast('Update reverted', 'info');
    } else {
      showToast('No previous update to revert', 'info');
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component={'form'}
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
        }}
      >
        <FollowUpChannelCard
          title={'Who gets this follow-up?'}
          subtitle={'Airship sends a tracked email to these addresses'}
          icon={<MailIcon width={20} height={20} />}
        >
          <EmailsInput
            emailContacts={emailContactsOf(contacts)}
            isLoading={!!isLoading}
          />
        </FollowUpChannelCard>

        <Controller
          control={control}
          name="followUpMessage"
          render={({ field, fieldState: { error } }) => (
            <FlexBox flexDirection="column" gap={2}>
              <Typography variant="body3">
                Your contact info and proposal link are added automatically.
              </Typography>
              <TextField
                multiline
                rows={10}
                fullWidth
                {...field}
                label="Follow up message"
                error={!!error}
                helperText={error?.message}
                value={field.value}
              />
              <FlexBox gap={2} flexWrap="wrap">
                <AIEditOptions
                  onClick={(rewriteOption) =>
                    improveFollowUpMessageHandler(field, rewriteOption)
                  }
                  disabled={!!isGenerating}
                  isLoadingOption={isGenerating}
                />
                <LoadingButton
                  color="softGold"
                  startIcon={<LeftArrowIcon />}
                  onClick={() => handleUndo(field)}
                  disabled={!!isGenerating}
                  loading={false}
                >
                  Undo update
                </LoadingButton>
              </FlexBox>
            </FlexBox>
          )}
        />

        <FollowUpNativeActions
          contacts={contacts}
          isLoading={!!isLoading}
          isLoadingChannel={isLoadingChannel}
          getShareLink={getShareLink}
        />
      </Box>
    </FormProvider>
  );
};

export default FollowUpSection;
